import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Ruler, ArrowRight, CheckCircle } from 'lucide-react';
import { setCanonical, setMeta } from '../utils/seo';
import { SponsorBanner, AdSensePlaceholder, ClickBankBanner } from './AdSlots';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FindMySize() {
  const [step, setStep] = useState(1);
  const [gender, setGender] = useState('');
  const [category, setCategory] = useState('');
  const [measurements, setMeasurements] = useState({ chest_cm: '', bust_cm: '', waist_cm: '', hip_cm: '', foot_cm: '' });
  const [unit, setUnit] = useState('cm');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setCanonical('/find-my-size');
    setMeta('Find My Size - Clothing Size Calculator', 'Enter your measurements and find your perfect size across 50+ brands. Free size calculator.');
  }, []);

  const categories = gender === 'male'
    ? [{ value: 'mens_tops', label: 'Tops / Shirts' }, { value: 'mens_pants', label: 'Pants / Jeans' }, { value: 'mens_shoes', label: 'Shoes' }]
    : [{ value: 'womens_tops', label: 'Tops / Dresses' }, { value: 'womens_shoes', label: 'Shoes' }];

  const isShoes = category.includes('shoes');
  const isMensTops = category === 'mens_tops';
  const isWomensTops = category === 'womens_tops';
  const isPants = category === 'mens_pants';

  const toInch = (cm) => (cm / 2.54).toFixed(1);
  const toCm = (inch) => (inch * 2.54).toFixed(1);

  const handleSubmit = async () => {
    setLoading(true);
    const data = { gender, category };
    if (isShoes) data.foot_cm = unit === 'cm' ? parseFloat(measurements.foot_cm) : parseFloat(toCm(measurements.foot_cm));
    if (isMensTops) data.chest_cm = unit === 'cm' ? parseFloat(measurements.chest_cm) : parseFloat(toCm(measurements.chest_cm));
    if (isWomensTops) data.bust_cm = unit === 'cm' ? parseFloat(measurements.bust_cm) : parseFloat(toCm(measurements.bust_cm));
    if (isPants) data.waist_cm = unit === 'cm' ? parseFloat(measurements.waist_cm) : parseFloat(toCm(measurements.waist_cm));
    try {
      const r = await axios.post(`${API}/size/quiz`, data);
      setResults(r.data);
      setStep(4);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-8 py-12">
      <div className="text-center mb-10">
        <Ruler className="w-8 h-8 text-[#C5A028] mx-auto mb-4" />
        <h1 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-light tracking-tighter text-[#1C1917] mb-4">Find My Size</h1>
        <p className="text-[#78716C]">Enter your measurements and get your size across 50+ brands instantly.</p>
      </div>

      {/* Progress */}
      <div className="flex items-center justify-center gap-2 mb-10">
        {[1, 2, 3, 4].map(s => (
          <div key={s} className="flex items-center gap-2">
            <div className={`w-8 h-8 flex items-center justify-center text-xs font-bold border-2 transition-all ${step >= s ? 'bg-[#C5A028] text-white border-[#C5A028]' : 'bg-white text-[#78716C] border-[#E8E2D9]'}`}>{s}</div>
            {s < 4 && <div className={`w-8 h-0.5 ${step > s ? 'bg-[#C5A028]' : 'bg-[#E8E2D9]'}`} />}
          </div>
        ))}
      </div>

      {/* Step 1: Gender */}
      {step === 1 && (
        <div className="space-y-4">
          <h2 className="font-['Cormorant_Garamond'] text-2xl font-semibold text-[#1C1917] text-center mb-6">I'm shopping for...</h2>
          <div className="grid grid-cols-2 gap-4">
            {[{ v: 'male', l: "Men's Clothing" }, { v: 'female', l: "Women's Clothing" }].map(g => (
              <button key={g.v} onClick={() => { setGender(g.v); setCategory(''); setStep(2); }} className={`p-6 border-2 text-center transition-all hover:border-[#C5A028] hover:shadow-md ${gender === g.v ? 'border-[#C5A028] bg-[#FAF8F4]' : 'border-[#E8E2D9]'}`} data-testid={`gender-${g.v}`}>
                <p className="font-semibold text-[#1C1917]">{g.l}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Step 2: Category */}
      {step === 2 && (
        <div className="space-y-4">
          <h2 className="font-['Cormorant_Garamond'] text-2xl font-semibold text-[#1C1917] text-center mb-6">What are you shopping for?</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {categories.map(c => (
              <button key={c.value} onClick={() => { setCategory(c.value); setStep(3); }} className={`p-5 border-2 text-center transition-all hover:border-[#C5A028] hover:shadow-md ${category === c.value ? 'border-[#C5A028] bg-[#FAF8F4]' : 'border-[#E8E2D9]'}`} data-testid={`cat-${c.value}`}>
                <p className="font-semibold text-[#1C1917]">{c.label}</p>
              </button>
            ))}
          </div>
          <button onClick={() => setStep(1)} className="text-sm text-[#78716C] hover:text-[#C5A028] mt-4" data-testid="quiz-back-1">Back</button>
        </div>
      )}

      {/* Step 3: Measurements */}
      {step === 3 && (
        <div className="space-y-6">
          <h2 className="font-['Cormorant_Garamond'] text-2xl font-semibold text-[#1C1917] text-center mb-2">Enter your measurements</h2>
          <div className="flex justify-center gap-2 mb-6">
            <button onClick={() => setUnit('cm')} className={`px-4 py-2 text-xs font-medium border ${unit === 'cm' ? 'bg-[#1C1917] text-white border-[#1C1917]' : 'border-[#E8E2D9]'}`} data-testid="unit-cm">Centimeters</button>
            <button onClick={() => setUnit('in')} className={`px-4 py-2 text-xs font-medium border ${unit === 'in' ? 'bg-[#1C1917] text-white border-[#1C1917]' : 'border-[#E8E2D9]'}`} data-testid="unit-in">Inches</button>
          </div>
          <div className="max-w-sm mx-auto space-y-4">
            {isShoes && (
              <div>
                <label className="block text-sm font-medium text-[#1C1917] mb-1">Foot Length ({unit})</label>
                <input type="number" step="0.1" value={measurements.foot_cm} onChange={e => setMeasurements({ ...measurements, foot_cm: e.target.value })} className="w-full p-3 border border-[#E8E2D9] bg-[#FAFAFA] focus:border-[#C5A028] focus:outline-none" placeholder={unit === 'cm' ? 'e.g. 26.5' : 'e.g. 10.4'} data-testid="input-foot" />
              </div>
            )}
            {isMensTops && (
              <div>
                <label className="block text-sm font-medium text-[#1C1917] mb-1">Chest ({unit})</label>
                <input type="number" step="0.1" value={measurements.chest_cm} onChange={e => setMeasurements({ ...measurements, chest_cm: e.target.value })} className="w-full p-3 border border-[#E8E2D9] bg-[#FAFAFA] focus:border-[#C5A028] focus:outline-none" placeholder={unit === 'cm' ? 'e.g. 96' : 'e.g. 37.8'} data-testid="input-chest" />
              </div>
            )}
            {isWomensTops && (
              <div>
                <label className="block text-sm font-medium text-[#1C1917] mb-1">Bust ({unit})</label>
                <input type="number" step="0.1" value={measurements.bust_cm} onChange={e => setMeasurements({ ...measurements, bust_cm: e.target.value })} className="w-full p-3 border border-[#E8E2D9] bg-[#FAFAFA] focus:border-[#C5A028] focus:outline-none" placeholder={unit === 'cm' ? 'e.g. 88' : 'e.g. 34.6'} data-testid="input-bust" />
              </div>
            )}
            {isPants && (
              <div>
                <label className="block text-sm font-medium text-[#1C1917] mb-1">Waist ({unit})</label>
                <input type="number" step="0.1" value={measurements.waist_cm} onChange={e => setMeasurements({ ...measurements, waist_cm: e.target.value })} className="w-full p-3 border border-[#E8E2D9] bg-[#FAFAFA] focus:border-[#C5A028] focus:outline-none" placeholder={unit === 'cm' ? 'e.g. 82' : 'e.g. 32.3'} data-testid="input-waist" />
              </div>
            )}
          </div>
          <div className="flex justify-center gap-4 mt-6">
            <button onClick={() => setStep(2)} className="px-6 py-3 border border-[#E8E2D9] text-sm font-medium text-[#78716C] hover:border-[#C5A028] transition-colors" data-testid="quiz-back-2">Back</button>
            <button onClick={handleSubmit} disabled={loading} className="px-8 py-3 bg-[#1C1917] text-white text-sm font-medium hover:bg-[#C5A028] transition-colors disabled:opacity-50" data-testid="quiz-submit">
              {loading ? 'Finding...' : 'Find My Size'}
            </button>
          </div>
        </div>
      )}

      {/* Step 4: Results */}
      {step === 4 && results && (
        <div>
          <div className="text-center mb-8">
            <CheckCircle className="w-10 h-10 text-[#C5A028] mx-auto mb-3" />
            <h2 className="font-['Cormorant_Garamond'] text-2xl font-semibold text-[#1C1917] mb-2">Your Size Results</h2>
            <p className="text-sm text-[#78716C]">Based on your measurements, here's your size across brands:</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {results.recommendations?.map((r, i) => (
              <Link key={i} to={`/brand/${r.slug}`} className="bg-white p-4 border border-[#E8E2D9] hover:border-[#C5A028] transition-all group" data-testid={`result-${r.slug}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold text-[#1C1917] group-hover:text-[#C5A028]">{r.brand}</span>
                  <span className="text-lg font-bold text-[#C5A028]">{r.size?.label || r.size?.us}</span>
                </div>
                <div className="flex gap-3 text-xs text-[#78716C]">
                  {r.size?.us && <span>US: {r.size.us}</span>}
                  {r.size?.uk && <span>UK: {r.size.uk}</span>}
                  {r.size?.eu && <span>EU: {r.size.eu}</span>}
                </div>
                {r.fit_note && <p className="text-xs text-[#78716C] mt-2 italic">{r.fit_note}</p>}
              </Link>
            ))}
          </div>
          {results.recommendations?.length === 0 && (
            <p className="text-center text-[#78716C] py-8">No exact matches found. Try adjusting your measurements slightly, or browse our <Link to="/brands" className="text-[#C5A028] hover:underline">brand directory</Link>.</p>
          )}
          <div className="text-center mt-8">
            <button onClick={() => { setStep(1); setResults(null); setGender(''); setCategory(''); setMeasurements({ chest_cm: '', bust_cm: '', waist_cm: '', hip_cm: '', foot_cm: '' }); }} className="px-6 py-3 border border-[#E8E2D9] text-sm font-medium text-[#78716C] hover:border-[#C5A028]" data-testid="quiz-restart">Start Over</button>
          </div>
        </div>
      )}

      <div className="mt-12 text-xs text-[#78716C] text-center">Sizes are approximate. Always verify with the retailer before purchasing.</div>
      <SponsorBanner />
      <ClickBankBanner />
      <AdSensePlaceholder />
    </div>
  );
}
